import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CalendarPlusIcon, CheckIcon, ClockIcon, CreditCardIcon, HeartPulseIcon, MapPinIcon, PillIcon, SearchIcon, SparklesIcon, UsersIcon } from 'lucide-react';
import { declaredAllergies, inscriptionCategories, inscriptionChild, type ActivitySlot } from '../../data/loisirs';
import type { InscriptionKind } from '../../hooks/useInscriptionConversation';
interface InscriptionMessageProps {
  kind: InscriptionKind;
  live: boolean;
  category: string | null;
  slots: ActivitySlot[];
  slot: ActivitySlot | null;
  newAllergy: string | null;
  onPickCategory: (category: string) => void;
  onRegister: (slot: ActivitySlot) => void;
  onConfirmAllergies: (declaration: string | null) => void;
  onRestart: () => void;
}
const eyebrow = 'flex items-center gap-2 text-sm font-bold text-ville-indigo';
const chipButton = 'rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:bg-ville-indigo-soft hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo';
function SlotDetails({
  slot
}: {
  slot: ActivitySlot;
}) {
  return <dl className="mt-2 space-y-1 text-[13px] text-slate-600" data-id="element-2029" data-mp="ywhtv2luvk2m">
      <div className="flex items-center gap-1.5" data-id="element-2030" data-mp="tuipbpedqh43">
        <ClockIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" data-id="element-2031" data-mp="ow7htlbk4ip1" />
        <dt className="sr-only" data-id="element-2032" data-mp="oy23x2ykegup">Horaire</dt>
        <dd data-id="element-2033" data-mp="k5914kmnpil9">
          {slot.day} · {slot.time}
        </dd>
      </div>
      <div className="flex items-center gap-1.5" data-id="element-2034" data-mp="g3nq74tonedp">
        <MapPinIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" data-id="element-2035" data-mp="g4f8xf26lhmk" />
        <dt className="sr-only" data-id="element-2036" data-mp="b63z34bvz74c">Lieu</dt>
        <dd data-id="element-2037" data-mp="b2zpfrfnjj6a">{slot.place}</dd>
      </div>
      <div className="flex items-center gap-1.5" data-id="element-2038" data-mp="65zileazace4">
        <UsersIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" data-id="element-2039" data-mp="67u7f3pdeonh" />
        <dt className="sr-only" data-id="element-2040" data-mp="17eq611ywx08">Âge et places</dt>
        <dd data-id="element-2041" data-mp="3nnvjzwieoor0">
          {slot.audience} · {slot.spots}
        </dd>
      </div>
    </dl>;
}
export function InscriptionMessage({
  kind,
  live,
  category,
  slots,
  slot,
  newAllergy,
  onPickCategory,
  onRegister,
  onConfirmAllergies,
  onRestart
}: InscriptionMessageProps) {
  if (kind === 'activite') {
    return <div data-id="element-2042" data-mp="3iq3nep6tgegh">
        <p className={eyebrow} data-id="element-2043" data-mp="3iplsbvvw46a5">
          <SparklesIcon className="h-4 w-4" aria-hidden="true" data-id="element-2044" data-mp="a9igrf1npxgj" />
          Inscription
        </p>
        <p className="mt-2 text-[17px] font-bold text-ville-navy" data-id="element-2045" data-mp="f8lmz2tdi9q3">Quelle activité voulez-vous ?</p>
        <p className="mt-1 text-sm text-slate-500" data-id="element-2046" data-mp="1h6snzu0mm3q">
          Je cherche les places libres pour {inscriptionChild.name} ({inscriptionChild.age}).
        </p>
        {live && <ul className="mt-4 flex flex-wrap gap-2" data-id="element-2047" data-mp="5gpd6uhxcv61">
            {inscriptionCategories.map(option => <li key={option} data-id="element-2048" data-mp="t6jov8qicdop">
                <button type="button" onClick={() => onPickCategory(option)} className={chipButton} data-id="element-2049" data-mp="xzcudytsvq1u">
                  {option}
                </button>
              </li>)}
          </ul>}
      </div>;
  }
  if (kind === 'resultats') {
    const [best, ...others] = slots;
    if (!best) {
      return <div data-id="element-2050" data-mp="2ses2fz0izpo1">
          <p className={eyebrow} data-id="element-2051" data-mp="2sejr4bk1wsey">
            <SearchIcon className="h-4 w-4" aria-hidden="true" data-id="element-2052" data-mp="2ngi79oeswp2f" />
            Recherche
          </p>
          <p className="mt-2 text-[17px] font-bold text-ville-navy" data-id="element-2053" data-mp="1zqe1bae54d89">
            Aucune place trouvée pour cette activité.
          </p>
          <p className="mt-1 text-sm text-slate-500" data-id="element-2054" data-mp="1usuo8dt6i6hw">Nommez une autre activité et je regarde de nouveau.</p>
        </div>;
    }
    return <div data-id="element-2055" data-mp="24pqojw8xbmbd">
        <p className={eyebrow} data-id="element-2056" data-mp="24q0drhm59pg0">
          <SearchIcon className="h-4 w-4" aria-hidden="true" data-id="element-2057" data-mp="24opftyfdrbwt" />
          {slots.length} places trouvées en {category}
        </p>
        <p className="mt-2 text-[17px] font-bold text-ville-navy" data-id="element-2058" data-mp="3l25txhlgft0r">
          Voici ce que je recommande pour {inscriptionChild.name}
        </p>

        <article className="mt-4 rounded-2xl border-2 border-ville-navy bg-ville-sand p-4" data-id="element-2059" data-mp="1pobo8jyaqssw">
          <div className="flex flex-wrap items-center gap-2" data-id="element-2060" data-mp="1umd8v9bdf06h">
            <span className="rounded-full bg-ville-lime px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-ville-navy" data-id="element-2061" data-mp="1uozh7b635jpr">
              Recommandé
            </span>
            <p className="text-[15px] font-extrabold text-ville-navy" data-id="element-2062" data-mp="23p4o7k7dag3e">{best.name}</p>
          </div>
          <SlotDetails slot={best} data-id="element-2063" data-mp="28m4spqokjase" />
          {best.note && <p className="mt-2 text-[13px] font-semibold text-ville-deep" data-id="element-2064" data-mp="2dlhgqer6enwk">{best.note}</p>}
          <p className="mt-2 text-[13px] font-bold text-ville-navy" data-id="element-2065" data-mp="2icfx1ec5zlzt">{best.price}</p>
          {live && <button type="button" onClick={() => onRegister(best)} className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2 sm:w-auto" data-id="element-2066" data-mp="2mdt6dyegu7bh">
              <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-2067" data-mp="31gb2z61wfbnl" />
              Inscrire {inscriptionChild.name} — 1 clic
            </button>}
        </article>

        {others.length > 0 && <ul className="mt-3 space-y-2" data-id="element-2068" data-mp="2shpkfawffwmt">
            {others.map(option => <li key={option.id} data-id="element-2069" data-mp="3p5ltzvtg5roj">
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 px-4 py-3" data-id="element-2070" data-mp="3p5vpu016at25">
                  <div className="min-w-0" data-id="element-2071" data-mp="3fglumbb18u9t">
                    <p className="text-sm font-bold text-ville-navy" data-id="element-2072" data-mp="3ffj2k01z4lsb">
                      {option.day} · {option.time}
                    </p>
                    <p className="truncate text-xs text-slate-500" data-id="element-2073" data-mp="brquew1owgw0">
                      {option.place} · {option.spots}
                    </p>
                  </div>
                  {live && <button type="button" onClick={() => onRegister(option)} className={chipButton} data-id="element-2074" data-mp="218w5cg34r5ty">
                      Choisir
                    </button>}
                </div>
              </li>)}
          </ul>}
      </div>;
  }
  if (kind === 'allergies') {
    return <div data-id="element-2075" data-mp="1whnv5htz7gfq">
        <p className={eyebrow} data-id="element-2076" data-mp="1whe925c1xtfb">
          <HeartPulseIcon className="h-4 w-4" aria-hidden="true" data-id="element-2077" data-mp="1wgcuvrzqoqtd" />
          Dossier médical
        </p>
        <p className="mt-2 text-[17px] font-bold text-ville-navy" data-id="element-2078" data-mp="1ih3gmxv3g6s1">
          Y a-t-il de nouvelles allergies à déclarer pour {inscriptionChild.name} ?
        </p>
        <p className="mt-1 text-sm text-slate-500" data-id="element-2079" data-mp="18la59ht42jbu">
          Voici ce qui est déjà au dossier. Les monitrices y ont accès pendant l’activité.
        </p>

        <ul className="mt-4 space-y-2" data-id="element-2080" data-mp="1dl4snbdogqs5">
          {declaredAllergies.map(allergy => <li key={allergy.id} className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-ville-sand px-4 py-3" data-id="element-2081" data-mp="32wf3g6lfi3js">
              <PillIcon className="mt-0.5 h-4 w-4 shrink-0 text-ville-deep" aria-hidden="true" data-id="element-2082" data-mp="wais6sr765y4" />
              <div className="min-w-0" data-id="element-2083" data-mp="rchbgeh8uipt">
                <p className="text-sm font-bold text-ville-navy" data-id="element-2084" data-mp="15zska8m1jom8">
                  {allergy.name}
                  <span className="ml-2 rounded-full bg-white px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-slate-500" data-id="element-2085" data-mp="1192if5ctq3wa">
                    {allergy.kind}
                  </span>
                </p>
                <p className="mt-0.5 text-[13px] text-slate-600" data-id="element-2086" data-mp="1azez2mhipcx5">{allergy.detail}</p>
              </div>
            </li>)}
        </ul>

        {live && <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-2087" data-mp="1owka70w391z9">
            <button type="button" onClick={() => onConfirmAllergies(null)} className="inline-flex items-center gap-2 rounded-full bg-ville-navy px-6 py-2.5 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-2088" data-mp="1k0v92t4f7d0u">
              <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-2089" data-mp="34v9idno3sns6" />
              Rien de nouveau
            </button>
            <p className="text-[13px] text-slate-500" data-id="element-2090" data-mp="39pnj85ljbn48">
              Sinon, écrivez la nouvelle allergie dans le champ ci-dessous.
            </p>
          </div>}
      </div>;
  }
  return <div data-id="element-2091" data-mp="2h0zs3te3u3ir">
      <span className="grid h-11 w-11 place-items-center rounded-full bg-ville-mist text-ville-deep" aria-hidden="true" data-id="element-2092" data-mp="2ly00wc2c9kdk">
        <CheckIcon className="h-6 w-6" data-id="element-2093" data-mp="2qw1hmqcal7lv" />
      </span>
      <h3 className="mt-3 text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-2094" data-mp="2vw61mqceskgu">
        {inscriptionChild.name} est inscrite
      </h3>
      {slot && <>
          <p className="mt-1 text-[15px] font-bold text-ville-navy" data-id="element-2095" data-mp="66kkciinpfap">{slot.name}</p>
          <SlotDetails slot={slot} data-id="element-2096" data-mp="b4lziscyougw" />
          <p className="mt-3 flex items-center gap-1.5 text-[13px] text-slate-600" data-id="element-2097" data-mp="b7rtakwjckiy">
            <CreditCardIcon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" data-id="element-2098" data-mp="1vq2a46uk5yhh" />
            {slot.price} · payé avec la carte enregistrée
          </p>
          <p className="mt-1 text-xs text-slate-500" data-id="element-2099" data-mp="1lx4o3qqcxre4">
            Confirmation envoyée par courriel · place réservée avec la carte citoyenne de la famille
          </p>
          <p className="mt-3 flex items-start gap-1.5 text-[13px] text-slate-600" data-id="element-2100" data-mp="1bzqkwstg2z3r">
            <HeartPulseIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" data-id="element-2101" data-mp="17yepu7rsxlo6" />
            {newAllergy ? `Nouvelle allergie ajoutée au dossier : ${newAllergy}` : `Dossier médical inchangé (${declaredAllergies.map(allergy => allergy.name).join(', ')})`}
          </p>
        </>}
      {live && <div className="mt-5 flex flex-wrap items-center gap-3" data-id="element-2102" data-mp="2xc0do7qjld9g">
          <Link to="/mes-loisirs" className="inline-flex items-center gap-2 rounded-full bg-ville-navy px-6 py-2.5 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5" data-id="element-2103" data-mp="2sdqgjrl9c2l7">
            Voir mes activités
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-2104" data-mp="2ij9hj3atuwrw" />
          </Link>
          <button type="button" className={`inline-flex items-center gap-2 ${chipButton}`} data-id="element-2105" data-mp="2ih5dmlcxhxrm">
            <CalendarPlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-2106" data-mp="2efjw2mpg8vb3" />
            Ajouter au calendrier
          </button>
          <button type="button" onClick={onRestart} className={chipButton} data-id="element-2107" data-mp="1lsz8lxc3h3ct">
            Inscrire à autre chose
          </button>
        </div>}
    </div>;
}